import React, { useState, useEffect, useMemo } from 'react'
import { Box, Tabs, Tab, Badge, TextField, InputAdornment, CircularProgress } from '@mui/material'
import Search from '@mui/icons-material/Search'
import { useNavigate } from 'react-router-dom'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import KycApprovalsList from './components/KycApprovalsList'
import { kycApprovalAPI } from './services/kycApprovalService'
import { OVERALL_STATUS, getStatusLabel } from './utils/kycApprovalUtils'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { toast } from 'react-toastify'

/**
 * Tab configuration for KYC approvals listing
 */
const TAB_CONFIG = [
  {
    id: 'approved',
    label: 'Approved',
    status: OVERALL_STATUS.APPROVED,
  },
  {
    id: 'pending',
    label: 'Pending',
    statusFilter: (status) => status !== OVERALL_STATUS.APPROVED && status !== OVERALL_STATUS.REJECTED,
  },
  {
    id: 'create',
    label: 'Create New',
    isTherapistListing: true,
  },
]

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

export default function KycApprovalsListing() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')

  // Tab data state
  const [tabsData, setTabsData] = useState({
    approved: { data: [], loading: true },
    pending: { data: [], loading: true },
    create: { data: [], loading: true },
  })

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setSearchQuery('')
  }

  const updateTabData = (tabId, data, loading = false) => {
    setTabsData((prev) => ({
      ...prev,
      [tabId]: { data, loading },
    }))
  }

  /**
   * Fetch approval requests
   */
  const fetchApprovalRequests = async () => {
    try {
      // Fetch all requests
      const allResp = await kycApprovalAPI.listRequests()

      if (allResp.status === 200 && allResp.data) {
        const allRequests = allResp.data

        // Filter for approved tab
        const approved = allRequests.filter(
          (req) => req.overallStatus === OVERALL_STATUS.APPROVED,
        )

        // Filter for pending tab (everything except APPROVED and REJECTED)
        const pending = allRequests.filter(
          (req) =>
            req.overallStatus !== OVERALL_STATUS.APPROVED &&
            req.overallStatus !== OVERALL_STATUS.REJECTED,
        )

        updateTabData('approved', approved, false)
        updateTabData('pending', pending, false)
      } else {
        updateTabData('approved', [], false)
        updateTabData('pending', [], false)
        toast.error('Failed to fetch approval requests')
      }
    } catch (error) {
      console.error('Error fetching approval requests:', error)
      updateTabData('approved', [], false)
      updateTabData('pending', [], false)
      toast.error('Error fetching approval requests')
    }
  }

  /**
   * Fetch all therapists for Create New tab
   */
  const fetchTherapists = async () => {
    try {
      const resp = await getSPDataWithParams('/api/secure/cc/support/therapists/list')

      if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)?.mentor || []
        updateTabData('create', data, false)
      } else {
        updateTabData('create', [], false)
      }
    } catch (error) {
      console.error('Error fetching therapists:', error)
      updateTabData('create', [], false)
      toast.error('Error fetching therapists')
    }
  }

  /**
   * Initial data fetch
   */
  useEffect(() => {
    fetchApprovalRequests()
    fetchTherapists()
  }, [])

  /**
   * Filter data by search query
   */
  const filterBySearch = (list) => {
    if (!searchQuery.trim()) return list

    const query = searchQuery.toLowerCase()
    return list.filter((item) => {
      const name = item.therapistName || item.name || ''
      const phone = item.therapistPhone || item.phone || ''
      const appRef = item.applicationRef || ''

      return (
        name.toLowerCase().includes(query) ||
        phone.includes(query) ||
        appRef.toLowerCase().includes(query)
      )
    })
  }

  /**
   * Get filtered data for current tab
   */
  const filteredTabsData = useMemo(() => {
    return {
      approved: filterBySearch(tabsData.approved.data),
      pending: filterBySearch(tabsData.pending.data),
      create: filterBySearch(tabsData.create.data),
    }
  }, [tabsData, searchQuery])

  /**
   * Handle item click - navigate to form
   */
  const handleItemClick = (item, tabId) => {
    if (tabId === 'create') {
      // Therapist from create tab - navigate to new form
      navigate(`/kyc-approvals/form/new/${item.id}`)
    } else {
      // Existing request - navigate to edit form
      navigate(`/kyc-approvals/form/${item.id}`)
    }
  }

  const currentTabConfig = TAB_CONFIG[activeTab]
  const currentTabData = tabsData[currentTabConfig.id]
  const currentFilteredData = filteredTabsData[currentTabConfig.id]

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.default',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <SupportHeader heading="KYC Approvals" />

        <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              {TAB_CONFIG.map((tab, index) => (
                <Tab
                  key={tab.id}
                  label={
                    <Badge
                      badgeContent={tabsData[tab.id]?.data.length || 0}
                      color="primary"
                    >
                      {tab.label}
                    </Badge>
                  }
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              placeholder={`Search ${currentTabConfig.label.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ width: '100%' }}>
        {TAB_CONFIG.map((tab, index) => (
          <TabPanel key={tab.id} value={activeTab} index={index}>
            {currentTabData.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <KycApprovalsList
                items={currentFilteredData}
                mode={tab.isTherapistListing ? 'therapist-list' : 'request-list'}
                onItemClick={(item) => handleItemClick(item, tab.id)}
                tabLabel={tab.label}
              />
            )}
          </TabPanel>
        ))}
      </Box>
    </>
  )
}
